import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/fonts/fonts.css";
import "@/index.css";
import App from "@/App";
import { applyDynamicManifest } from "@/pwa/dynamicManifest";
import * as serviceWorkerRegistration from "@/serviceWorkerRegistration";
import { installScrollHints } from "@/lib/scrollHints";
import { installA11yHelpers } from "@/lib/a11y";
import { initInstall } from "@/lib/install";
import { listenForOpen } from "@/lib/deeplink";

applyDynamicManifest();

// New Zealand time for every clock time shown: stored times (sign-in times, message times, ...) are real moments, and without this a device whose own time zone is set
// wrongly (a tablet left on UTC, a laptop travelling) would show them in that zone. A caller that names its own zone is left alone. Plain dates are not touched.
["toLocaleTimeString", "toLocaleString"].forEach((fn) => {
  const original = Date.prototype[fn];
  Date.prototype[fn] = function nzLocale(locales, options) {   // eslint-disable-line no-extend-native
    return original.call(this, locales, options && options.timeZone ? options : { ...(options || {}), timeZone: "Pacific/Auckland" });
  };
});

// Escape closes the top-most modal/overlay: click its own Cancel / Close button so each form's usual
// close handling (reset state, refresh) still runs. Most overlays here are plain `fixed inset-0` panels
// that had no keyboard way out.
document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape" || e.defaultPrevented) return;
  const overlays = Array.from(document.querySelectorAll("div.fixed.inset-0")).filter((el) => el.offsetParent !== null || getComputedStyle(el).position === "fixed");
  const top = overlays[overlays.length - 1];
  if (!top) return;
  const buttons = Array.from(top.querySelectorAll("button"));
  const closer = buttons.find((b) => /(cancel|close)/i.test(b.getAttribute("data-testid") || "") || /^(cancel|close|×|✕)$/i.test((b.textContent || "").trim()) || /close/i.test(b.getAttribute("aria-label") || ""));
  if (closer && !closer.disabled) { e.preventDefault(); closer.click(); }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

installScrollHints();
installA11yHelpers();
initInstall();
listenForOpen();
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Registers public/service-worker.js for offline app-shell caching. Safe to
// call unconditionally -- it's a no-op outside secure/localhost contexts.
serviceWorkerRegistration.register();
